import { get } from 'es-toolkit/compat';
import type { Store } from 'redux';

import { getEnforcedLocale } from '../../features/app/lang/languageConfiguration';
import messagesEN from '../../features/app/lang/locales/en-GB.json';
import { reportErrorToSentry } from '../setup/sentry';
import { trace } from '../setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { displayMessagesFetched, localeChanged } from './langSlice';

const MODULE_NAME = 'marketplace-web';

export const sendError = (exception: unknown, errorInfo?: object) => {
    console.error(exception, errorInfo);
    reportErrorToSentry(exception, {
        tags: {
            module: MODULE_NAME,
        },
        extra: errorInfo ? { ...errorInfo } : undefined,
    });
};

// Accept both module namespaces and directly imported message catalogs.
const normalizeDynamicImport = (imported: unknown) => get(imported, 'default', imported);

const displayMessageLoaders = import.meta.glob<Record<string, string>>(
    ['../../features/app/lang/locales/*.json', '!../../features/app/lang/locales/en-GB.json'],
    { import: 'default' }
);

const isDisplayMessageCatalog = (catalog: unknown): catalog is typeof messagesEN => {
    if (typeof catalog !== 'object' || catalog === null || Array.isArray(catalog)) {
        return false;
    }

    const messages = catalog as Record<string, unknown>;
    return Object.keys(messagesEN).every(key => typeof messages[key] === 'string');
};

const fetchDisplayMessagesWithRetry = async (
    fetchDisplayMessages: (locale: string) => Promise<typeof messagesEN>,
    locale: string
): Promise<typeof messagesEN> => {
    let lastError: unknown;

    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const displayMessages = await fetchDisplayMessages(locale);
            if (!isDisplayMessageCatalog(displayMessages)) {
                throw new Error(`Display messages for locale "${locale}" are not a valid catalog`);
            }
            return displayMessages;
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError;
};

export const importDisplayMessages = async (locale: string): Promise<typeof messagesEN> => {
    const localeLoader = displayMessageLoaders[`../../features/app/lang/locales/${locale}.json`];
    const imported =
        locale === DEFAULT_LOCALE
            ? messagesEN
            : await (localeLoader?.() ?? Promise.reject(new Error(`Unsupported locale "${locale}"`)));
    const messages = normalizeDynamicImport(imported);

    if (!isDisplayMessageCatalog(messages)) {
        throw new Error(`Display messages for locale "${locale}" are not a valid catalog`);
    }

    return messages;
};

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(getEnforcedLocale() || locale);

        try {
            const displayMessages = await fetchDisplayMessagesWithRetry(fetchDisplayMessages, supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error) {
            sendError(new Error(`Could not load display messages for locale "${supportedLocale}"`, { cause: error }), {
                locale: supportedLocale,
            });
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
        return Promise.resolve();
    };
